import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icons, IconKey } from '../../constants';
import { Board, UserProfile } from '../../types';
import { THEME_STYLES as THEME_STYLES_CONST } from '../../constants';
import { TEMPLATES, TEMPLATE_CATEGORIES, Template } from '../../utils/templates';

interface BoardGridProps {
    boards: Board[];
    userProfile?: UserProfile | null;
    activeTab: string;
    isTrash: boolean;
    searchQuery: string;
    boardTab: 'my-boards' | 'shared'; // Added boardTab
    openMenuId: string | null;
    setOpenMenuId: (id: string | null) => void;
    onSelectBoard: (id: string) => void;
    onCreateBoard: () => void;
    onCreateBoardFromTemplate?: (template: Template) => void;
    onToggleFavorite: (id: string) => void;
    setEditingBoard: (board: Board) => void;
    setDeleteConfirmId: (id: string) => void;
    onOpenTemplateWizard?: () => void;
    onRestoreBoard: (id: string) => void;
    onDeleteForever: (id: string) => void;
    onDuplicateBoard: (id: string) => void;
    onShareBoard: (board: Board) => void;
    showCreateCard?: boolean;
}

export const BoardGrid: React.FC<BoardGridProps> = ({
    boards,
    userProfile,
    activeTab,
    isTrash,
    // searchQuery, // unused now
    boardTab,
    openMenuId,
    setOpenMenuId,
    onSelectBoard,
    onCreateBoard,
    onCreateBoardFromTemplate,
    onOpenTemplateWizard,
    onToggleFavorite,
    setEditingBoard,
    setDeleteConfirmId,
    onRestoreBoard,
    onDeleteForever,
    onDuplicateBoard,
    onShareBoard,
    showCreateCard
}) => {
    const [templateCategory, setTemplateCategory] = useState<string | null>(null);
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Only navigate if no input is focused
            if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;

            const maxIndex = boards.length - 1;

            switch (e.key) {
                case 'ArrowRight':
                    setFocusedIndex(prev => (prev < maxIndex ? prev + 1 : 0));
                    break;
                case 'ArrowLeft':
                    setFocusedIndex(prev => (prev > 0 ? prev - 1 : maxIndex));
                    break;
                case 'Enter':
                    if (focusedIndex >= 0 && focusedIndex <= maxIndex) {
                        onSelectBoard(boards[focusedIndex].id);
                    }
                    break;
                case 'Escape':
                    setFocusedIndex(-1);
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [boards, focusedIndex, onSelectBoard]);

    const filteredTemplates = templateCategory
        ? TEMPLATES.filter(t => t.category === templateCategory)
        : TEMPLATES;

    // Helper to get icon
    const getIcon = (name: string) => {
        const Icon = Icons[name as IconKey] || Icons.File;
        return <Icon className="w-4 h-4" />;
    };

    const renderBoardCard = (board: Board, isShared: boolean = false, idx: number = -1) => {
        const theme = THEME_STYLES_CONST[board.themeColor || 'blue'] || THEME_STYLES_CONST['blue'];
        const liquid = theme.liquid;
        const isMenuOpen = openMenuId === board.id;

        return (
            <div
                key={board.id}
                onClick={() => !isTrash && onSelectBoard(board.id)}
                onMouseEnter={() => setFocusedIndex(idx)}
                className={`group relative aspect-[4/3] rounded-[2rem] transition-all duration-300 ${isTrash ? 'opacity-75 hover:opacity-100' : 'cursor-pointer hover:scale-[1.02] hover:-translate-y-1 hover:shadow-2xl'} ${isMenuOpen ? 'z-50' : 'z-0'} ${focusedIndex === idx ? 'ring-4 ring-blue-500/50 shadow-2xl -translate-y-1' : ''}`}
            >
                {/* Clipped Background & Decor Container */}
                <div className={`absolute inset-0 rounded-[2rem] overflow-hidden ${liquid.bg} ${liquid.border} border transition-colors duration-300`}>
                    {/* Decor Blob */}
                    <div className={`absolute -bottom-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-20 pointer-events-none bg-current ${liquid.text}`}></div>
                </div>

                {/* Content Container */}
                <div className="relative z-10 h-full p-5 flex flex-col justify-between">
                    {/* Shared Badge */}
                    {isShared && (
                        <div className="absolute top-4 left-4 z-20">
                            <span className={`text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 backdrop-blur-md bg-white/20 dark:bg-black/20 ${liquid.text} ring-1 ring-white/10`}>
                                <Icons.Users className="w-3 h-3" /> Shared
                            </span>
                        </div>
                    )}

                    {/* Header / Menu */}
                    <div className={`flex justify-between items-start mb-2 relative z-20 ${isShared ? 'pt-6' : ''}`}>
                        <div className="flex gap-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider backdrop-blur-md bg-white/20 dark:bg-black/20 ${liquid.text} ring-1 ring-white/10`}>
                                {board.itemCount ?? board.notes.length} items
                            </span>
                            {board.isFavorite && (
                                <div className="w-6 h-6 rounded-full bg-yellow-400 text-white flex items-center justify-center shadow-lg animate-in zoom-in spin-in duration-300">
                                    <Icons.Star className="w-3 h-3" fill="currentColor" />
                                </div>
                            )}
                        </div>

                        <div className="flex gap-1 relative board-menu-container">
                            {/* Quick Actions (Desktop only hover) */}
                            <div className="hidden lg:flex items-center gap-1 mr-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onToggleFavorite(board.id); }}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-md bg-white/20 dark:bg-black/20 ${board.isFavorite ? 'text-yellow-400' : 'text-white/60 hover:text-white'} hover:bg-white/30`}
                                    title={board.isFavorite ? 'Unfavorite' : 'Favorite'}
                                >
                                    <Icons.Star className="w-3.5 h-3.5" fill={board.isFavorite ? 'currentColor' : 'none'} />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); onShareBoard(board); }}
                                    className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-md bg-white/20 dark:bg-black/20 text-white/60 hover:text-white hover:bg-white/30"
                                    title="Copy Link"
                                >
                                    <Icons.Link className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); onDuplicateBoard(board.id); }}
                                    className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-md bg-white/20 dark:bg-black/20 text-white/60 hover:text-white hover:bg-white/30"
                                    title="Duplicate"
                                >
                                    <Icons.Copy className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            <button
                                onClick={(e) => { e.stopPropagation(); setOpenMenuId(isMenuOpen ? null : board.id); }}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${isMenuOpen ? 'bg-black text-white dark:bg-white dark:text-black shadow-lg scale-110' : `${liquid.text} hover:bg-black/5 dark:hover:bg-white/10 opacity-0 group-hover:opacity-100`}`}
                            >
                                <Icons.MoreHorizontal className="w-4 h-4" />
                            </button>

                            <AnimatePresence>
                                {isMenuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9, y: 5, x: 10 }}
                                        animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
                                        exit={{ opacity: 0, scale: 0.9, y: 5 }}
                                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                        className="absolute top-full right-0 mt-2 w-60 bg-white/90 dark:bg-[#121212]/95 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 dark:border-white/10 p-2 z-[100] origin-top-right ring-1 ring-black/5"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {isTrash ? (
                                            <div className="space-y-1">
                                                <div className="px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">Deleted Actions</div>
                                                <button onClick={() => { onRestoreBoard(board.id); setOpenMenuId(null); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl transition-all text-left">
                                                    <Icons.RefreshCcw className="w-4 h-4" />Restore
                                                </button>
                                                <button onClick={() => { onDeleteForever(board.id); setOpenMenuId(null); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-xl transition-all text-left">
                                                    <Icons.Trash className="w-4 h-4" />Delete Forever
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="px-3 py-2 flex items-center justify-between">
                                                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">Actions</span>
                                                    <div className="h-1 w-1 rounded-full bg-slate-300 dark:bg-slate-700" />
                                                </div>

                                                <div className="space-y-1">
                                                    <button onClick={() => { setEditingBoard(board); setOpenMenuId(null); }} className="w-full flex items-center gap-3 px-3 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white rounded-xl transition-all text-left group/item">
                                                        <Icons.Edit className="w-4 h-4 opacity-70 group-hover/item:opacity-100" />Edit Board
                                                    </button>
                                                    <button onClick={() => { onDuplicateBoard(board.id); setOpenMenuId(null); }} className="w-full flex items-center gap-3 px-3 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white rounded-xl transition-all text-left group/item">
                                                        <Icons.Copy className="w-4 h-4 opacity-70 group-hover/item:opacity-100" />Duplicate
                                                    </button>
                                                    <button onClick={() => { onShareBoard(board); setOpenMenuId(null); }} className="w-full flex items-center gap-3 px-3 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-xl transition-all text-left group/item">
                                                        <Icons.Link className="w-4 h-4 opacity-70 group-hover/item:opacity-100" />Copy Link
                                                    </button>
                                                    <button onClick={() => { onToggleFavorite(board.id); setOpenMenuId(null); }} className="w-full flex items-center gap-3 px-3 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-yellow-600 dark:hover:text-yellow-400 rounded-xl transition-all text-left group/item">
                                                        <Icons.StarSparkle className="w-4 h-4 opacity-70 group-hover/item:opacity-100" />{board.isFavorite ? 'Unfavorite' : 'Favorite'}
                                                    </button>
                                                </div>

                                                <div className="h-px bg-slate-100 dark:bg-white/5 my-2 mx-2" />

                                                <div className="px-3 py-1">
                                                    <span className="text-[10px] font-black uppercase tracking-wider text-rose-300 dark:text-rose-900/50">Danger</span>
                                                </div>

                                                <button onClick={() => { setDeleteConfirmId(board.id); setOpenMenuId(null); }} className="w-full flex items-center gap-3 px-3 py-2 text-xs font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:text-rose-600 dark:hover:text-rose-400 rounded-xl transition-all text-left group/item">
                                                    <Icons.Trash className="w-4 h-4 opacity-70 group-hover/item:opacity-100" />Move to Trash
                                                </button>
                                            </>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 flex-1 flex flex-col justify-center min-h-0 pointer-events-none">
                        <h3 title={board.title} className={`text-xl font-bold leading-tight line-clamp-2 mb-1 ${liquid.text} break-words pointer-events-auto`}>{board.title}</h3>
                        <p title={board.description} className={`text-xs font-medium line-clamp-2 opacity-80 leading-relaxed ${liquid.text} break-words pointer-events-auto`}>{board.description || 'No description provided.'}</p>
                    </div>

                    {/* Footer */}
                    <div className={`relative z-10 flex justify-between items-center mt-auto pt-4 border-t border-black/5 dark:border-white/5`}>
                        <div className="flex -space-x-2 overflow-hidden">
                            {/* Mocked improved avatars for Phase 2 */}
                            <div className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center text-[10px] font-bold text-white border-2 border-white dark:border-slate-900 shadow-sm ring-1 ring-black/5 transition-transform hover:scale-110 hover:z-10 cursor-help" title="John Doe">JD</div>
                            <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-[10px] font-bold text-white border-2 border-white dark:border-slate-900 shadow-sm ring-1 ring-black/5 transition-transform hover:scale-110 hover:z-10 cursor-help" title="Sarah Smith">SS</div>
                            <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[8px] font-bold text-slate-500 border-2 border-white dark:border-slate-900 shadow-sm ring-1 ring-black/5">+2</div>
                        </div>
                        <div className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 opacity-60 ${liquid.text}`}>
                            <Icons.Clock className="w-3 h-3" />{new Date(board.lastEdited).toLocaleDateString()}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {/* Boards Grid */}
            <div className="mb-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8">
                    {/* Create New Board Button (filtered by prop) */}
                    {showCreateCard && (
                        <button
                            onClick={onCreateBoard}
                            className="group relative aspect-[4/3] rounded-[2.5rem] bg-indigo-50/50 dark:bg-[#1a1a1a] hover:bg-indigo-100/50 dark:hover:bg-[#202020] transition-all duration-500 overflow-hidden text-left"
                        >
                            <div className="absolute inset-0 bg-grid-slate-200/50 dark:bg-grid-slate-800/50 [mask-image:linear-gradient(to_bottom,white,transparent)]" />

                            {/* Floating Elements Container */}
                            <div className="absolute inset-0 flex items-center justify-center translate-y-4 group-hover:translate-y-2 transition-transform duration-500">

                                {/* Card 1: Chart (Back Left) */}
                                <div className="absolute -translate-x-12 -translate-y-12 -rotate-12 group-hover:-translate-y-16 group-hover:-rotate-12 transition-all duration-500 delay-75">
                                    <div className="w-24 h-32 bg-white dark:bg-[#252525] rounded-xl shadow-xl shadow-indigo-500/10 border border-slate-100 dark:border-slate-800 p-3 flex flex-col gap-2">
                                        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                            <Icons.Activity className="w-4 h-4 text-emerald-500" />
                                        </div>
                                        <div className="space-y-1">
                                            <div className="h-1.5 w-12 bg-slate-100 dark:bg-slate-700 rounded-full" />
                                            <div className="h-1.5 w-8 bg-slate-100 dark:bg-slate-700 rounded-full" />
                                        </div>
                                        <div className="flex items-end gap-1 mt-auto h-10">
                                            <div className="w-2 bg-emerald-400 h-[60%] rounded-t-sm" />
                                            <div className="w-2 bg-emerald-400/50 h-[40%] rounded-t-sm" />
                                            <div className="w-2 bg-emerald-400/30 h-[80%] rounded-t-sm" />
                                        </div>
                                    </div>
                                </div>

                                {/* Card 2: Image (Back Right) */}
                                <div className="absolute translate-x-16 -translate-y-10 rotate-6 group-hover:-translate-y-14 group-hover:rotate-12 transition-all duration-500 delay-100">
                                    <div className="w-28 h-24 bg-white dark:bg-[#252525] rounded-xl shadow-xl shadow-blue-500/10 border border-slate-100 dark:border-slate-800 p-2">
                                        <div className="w-full h-full bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden relative">
                                            <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-white/50 backdrop-blur-sm flex items-center justify-center">
                                                <Icons.Image className="w-2.5 h-2.5 text-slate-500" />
                                            </div>
                                            <div className="w-full h-full bg-gradient-to-br from-blue-400 to-indigo-500 opacity-20" />
                                        </div>
                                    </div>
                                </div>

                                {/* Card 3: Color Palette (Center Back) */}
                                <div className="absolute -translate-x-2 -translate-y-16 rotate-0 group-hover:-translate-y-20 transition-all duration-500">
                                    <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/20 rounded-xl shadow-lg border border-amber-200/50 dark:border-amber-700/30 p-2 flex flex-col gap-1 items-center justify-center rotate-3">
                                        <span className="text-[10px] font-black text-amber-600 dark:text-amber-400">IDEA</span>
                                        <div className="w-8 h-8 rounded-full bg-white dark:bg-[#121212] flex items-center justify-center">
                                            <Icons.Lightbulb className="w-4 h-4 text-amber-500" />
                                        </div>
                                    </div>
                                </div>

                                {/* Card 4: Foreground Main Card */}
                                <div className="absolute translate-y-2 group-hover:translate-y-0 transition-transform duration-500 z-10">
                                    <div className="w-40 h-48 bg-white/80 dark:bg-[#202020]/90 backdrop-blur-xl rounded-2xl shadow-2xl shadow-slate-900/10 border border-white/50 dark:border-slate-700 p-4 flex flex-col">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                                                <Icons.Plus className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h4 className="text-xs font-black text-slate-900 dark:text-white leading-none">New Board</h4>
                                                <p className="text-[9px] font-bold text-slate-400 mt-0.5">Start fresh</p>
                                            </div>
                                        </div>

                                        <div className="space-y-2 flex-1">
                                            <div className="h-2 w-full bg-slate-100 dark:bg-slate-700 rounded-full" />
                                            <div className="h-2 w-2/3 bg-slate-100 dark:bg-slate-700 rounded-full" />
                                            <div className="h-2 w-3/4 bg-slate-100 dark:bg-slate-700 rounded-full" />
                                        </div>

                                        <div className="mt-auto pt-3 border-t border-slate-100 dark:border-slate-700/50 flex justify-between items-center">
                                            <div className="flex -space-x-1.5">
                                                <div className="w-4 h-4 rounded-full bg-pink-400 border border-white dark:border-[#202020]" />
                                                <div className="w-4 h-4 rounded-full bg-indigo-400 border border-white dark:border-[#202020]" />
                                            </div>
                                            <div className="w-4 h-4 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                                                <Icons.ArrowRight className="w-2.5 h-2.5 text-slate-400" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Glass Folder Front Layer Effect */}
                                <div className="absolute bottom-[-60px] w-56 h-32 bg-gradient-to-t from-white/90 to-transparent dark:from-[#121212]/90 dark:to-transparent z-20 pointer-events-none blur-xl" />
                            </div>

                            <div className="absolute bottom-6 left-6 z-30">
                                <h3 className="text-lg font-black text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                    Create Board
                                </h3>
                                <p className="text-xs font-bold text-slate-400 group-hover:text-blue-400/80 transition-colors">
                                    Click to start blank
                                </p>
                            </div>
                        </button>
                    )}

                    {boards.map((board, idx) => renderBoardCard(board, board.user_id !== undefined && board.user_id !== userProfile?.id, idx))}
                </div>

                {/* Empty State for Shared */}
                {!showCreateCard && boards.length === 0 && (
                    <div className="text-center py-16">
                        <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                            <Icons.Users className="w-10 h-10 text-slate-300 dark:text-slate-600" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-600 dark:text-slate-400 mb-2">No boards found</h3>
                        <p className="text-sm text-slate-400 dark:text-slate-500">
                            Try changing your filter or create a new board.
                        </p>
                    </div>
                )}
            </div>

            {/* Template Gallery Section */}
            {activeTab === 'dashboard' && !isTrash && boardTab === 'my-boards' && (
                <div className="mb-12">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h3 className="text-2xl font-black bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent flex items-center gap-3">
                                <span className="p-2 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                    <Icons.Grid className="w-5 h-5" />
                                </span>
                                Start from Template
                            </h3>
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-2 ml-1">
                                Kickstart your project with professionally designed layouts
                            </p>
                        </div>
                    </div>

                    {/* Category Filter */}
                    <div className="flex gap-3 mb-8 overflow-x-auto pb-4 no-scrollbar">
                        <button
                            onClick={() => setTemplateCategory(null)}
                            className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all whitespace-nowrap border ${templateCategory === null
                                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white shadow-lg shadow-slate-900/20'
                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                                }`}
                        >
                            All Templates
                        </button>
                        {TEMPLATE_CATEGORIES.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setTemplateCategory(cat.id)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold transition-all whitespace-nowrap border ${templateCategory === cat.id
                                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white shadow-lg shadow-slate-900/20'
                                    : 'bg-white dark:bg-[#121212] text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                                    }`}
                            >
                                {getIcon(cat.icon)}
                                {cat.name}
                            </button>
                        ))}
                    </div>

                    {/* Template Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                        {/* Custom Template Card */}
                        <div
                            onClick={onOpenTemplateWizard}
                            className="group relative bg-white dark:bg-[#121212] rounded-[2rem] border-2 border-dashed border-slate-200 dark:border-slate-800 overflow-hidden hover:border-blue-500/50 dark:hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer flex flex-col h-full"
                        >
                            <div className="h-40 w-full bg-slate-50 dark:bg-[#1a1a1a] relative flex items-center justify-center group-hover:bg-blue-50/50 dark:group-hover:bg-blue-900/10 transition-colors duration-300">
                                <div className="relative z-10 w-16 h-16 rounded-2xl bg-white dark:bg-[#121212] shadow-xl shadow-black/5 border border-slate-100 dark:border-slate-800 flex items-center justify-center text-slate-400 group-hover:text-blue-500 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                                    <Icons.Sparkles className="w-8 h-8" />
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 z-20">
                                    <div className="absolute inset-0 bg-white/60 dark:bg-black/60 backdrop-blur-sm" />
                                    <span className="relative z-30 px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                                        Open Wizard
                                    </span>
                                </div>
                            </div>
                            <div className="p-5 flex flex-col flex-1">
                                <h4 className="font-bold text-slate-900 dark:text-white mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                    Custom Template
                                </h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                                    Create a blank slate or build your own layout.
                                </p>
                            </div>
                        </div>

                        {filteredTemplates.map((template, _idx) => {
                            // Generate consistent visual styles based on category/index
                            const getGradient = (cat: string) => {
                                switch (cat) {
                                    case 'productivity': return 'from-orange-500/20 to-rose-500/20 text-orange-600 dark:text-orange-400';
                                    case 'meetings': return 'from-blue-500/20 to-cyan-500/20 text-blue-600 dark:text-blue-400';
                                    case 'creative': return 'from-purple-500/20 to-pink-500/20 text-purple-600 dark:text-purple-400';
                                    case 'strategy': return 'from-emerald-500/20 to-teal-500/20 text-emerald-600 dark:text-emerald-400';
                                    default: return 'from-slate-500/20 to-gray-500/20 text-slate-600 dark:text-slate-400';
                                }
                            };

                            const style = getGradient(template.category || 'other');
                            // Extract colors for text mapping
                            let textClass = 'text-slate-500';
                            if (style.includes('text-orange')) textClass = 'text-orange-500';
                            else if (style.includes('text-blue')) textClass = 'text-blue-500';
                            else if (style.includes('text-purple')) textClass = 'text-purple-500';
                            else if (style.includes('text-emerald')) textClass = 'text-emerald-500';

                            return (
                                <div
                                    key={template.id}
                                    onClick={() => onCreateBoardFromTemplate?.(template)}
                                    className="group relative bg-white dark:bg-[#121212] rounded-[2rem] border border-slate-100 dark:border-slate-800 overflow-hidden hover:shadow-2xl hover:shadow-slate-200/50 dark:hover:shadow-black/50 transition-all duration-500 hover:-translate-y-1 cursor-pointer flex flex-col h-full"
                                >
                                    {/* Preview Area */}
                                    <div className={`h-40 w-full relative overflow-hidden bg-slate-50 dark:bg-[#1a1a1a]`}>

                                        {/* Abstract Background Blob */}
                                        <div className={`absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br ${style.split(' text')[0]} rounded-full blur-3xl opacity-60 group-hover:opacity-100 transition-opacity duration-500`} />

                                        {/* Content Container */}
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className={`w-16 h-16 rounded-2xl bg-white dark:bg-[#202020] shadow-xl shadow-black/5 border border-white/50 dark:border-white/5 flex items-center justify-center ${textClass} group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-500 relative z-10`}>
                                                {React.cloneElement(getIcon(template.iconName), { className: "w-8 h-8" })}
                                            </div>
                                        </div>

                                        {/* Hover Action Overlay */}
                                        <div className="absolute inset-0 z-20 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                                            <div className="absolute inset-0 bg-white/80 dark:bg-black/80 backdrop-blur-[2px]" />
                                            <button className="relative z-30 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl text-xs font-black shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 flex items-center gap-2">
                                                Use Template <Icons.ArrowRight className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-5 flex flex-col flex-1 relative z-10">
                                        <div className="flex items-start justify-between mb-2">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 dark:bg-slate-800/50 px-2 py-1 rounded-lg">
                                                {template.category}
                                            </span>
                                        </div>

                                        <h4 className="font-extrabold text-slate-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors text-base">
                                            {template.name}
                                        </h4>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                                            {template.description}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};
